<script setup lang="ts">
import BwrButton from './Button.vue'
import BwrMessage from './Message.vue'
import IconPercent from './icons/IconPercent.vue'
import IconThumbsup from './icons/IconThumbsup.vue'
import IconTick from './icons/IconTick.vue'
import IconSubscription from './icons/IconSubscription.vue'
import IconStatusError from './icons/IconStatusError.vue'
import IconStatusWarning from './icons/IconStatusWarning.vue'
import IconPencil from './icons/IconPencil.vue'
import IconPlasticBottle from '../icons/plastic-bottle.svg?component'
import BwrSelect from './form-fields/Select.vue'
import BwrRadio from './form-fields/Radio.vue'
import PurchaseLoading from './PurchaseLoading.vue'
import QuantityControl from './QuantityControl.vue'
import TrustpilotStars from './TrustpilotStars.vue'

import money from '../lib/formatters/money'
import * as DateUtils from '../lib/utils/recharge-dates'

import type { User } from '@/lib/types/User'
import type { Product } from '@/lib/types/Product'
import { computed } from 'vue'
import type { PurchaseData } from '@/lib/types/PurchaseData'

const props = defineProps<{
  price: number
  subscribePrice: number | null
  pricePer: {
    price: number
    unit: string
    subscribePrice: number | null
  } | null
  compareAtPrice: string | null
  allowSubscribe: boolean
  savePercent: number
  selectedSellingPlanId: string | null
  sellingPlanAllocations:
    | Product['variants'][number]['sellingPlanAllocations']
    | null
  quantity: number
  nextOrderDate: string | null
  when: 'now' | 'next'
  isLoggedIn: boolean
  loginLink: string
  userLoadState: string
  subscriptionToThisProduct: User['subscriptions'][number] | null
  customerPortalLink: string
  nextOrderLink: string
  purchaseState: string
  inTab: boolean
  showInlineVariantSelector: boolean
  variants: Product['variants']
  variantOptionLabel: string
  selectedVariantId: string
  available: boolean
  preSale?: boolean
  waitlistPageUrl?: string
  url: string
  plasticWasteSaving?: {
    sku: string
    plastic_waste_saved_g: number
  }
  relatedProductReviews: string | null
  showLink: boolean
}>()

const emit = defineEmits<{
  (e: 'purchase', purchaseOptions?: Partial<PurchaseData>): void
  (e: 'variant:changed', variantId: string): void
  (e: 'when:changed', when: 'now' | 'next'): void
  (e: 'quantity:changed', quantity: number): void
  (e: 'selectedSellingPlan:changed', sellingPlanId: string | null): void
  (e: 'viewProduct'): void
}>()

const subscribeAndSave = props.savePercent > 0

const hasSubscriptions = computed(() => nextOrderDate.value !== null)

const nextOrderDate = computed(() => {
  if (props.nextOrderDate) {
    const date = DateUtils.parse(props.nextOrderDate)
    if (date) {
      return DateUtils.format(date)
    } else {
      return null
    }
  } else {
    return null
  }
})

const nextDeliveryOfThisProduct = computed(() => {
  if (
    props.subscriptionToThisProduct &&
    props.subscriptionToThisProduct.next_charge_scheduled_at
  ) {
    const date = DateUtils.parse(
      props.subscriptionToThisProduct.next_charge_scheduled_at,
    )
    return date ? DateUtils.format(date) : null
  } else {
    return null
  }
})

const onVariantChange = function (variantId: string) {
  if (props.purchaseState !== 'IN_PROGRESS') emit('variant:changed', variantId)
}

const onQuantityChange = function (quantity: number) {
  if (props.purchaseState !== 'IN_PROGRESS') emit('quantity:changed', quantity)
}

const onSellingPlanChange = function (sellingPlanId: string | null) {
  if (props.purchaseState !== 'IN_PROGRESS')
    emit('selectedSellingPlan:changed', sellingPlanId)
}

const onWhenChange = function (when: 'now' | 'next') {
  if (props.purchaseState !== 'IN_PROGRESS') emit('when:changed', when)
}

const plasticWasteSaved = computed(() => {
  if (props.plasticWasteSaving) {
    return props.plasticWasteSaving.plastic_waste_saved_g / 1000
  } else {
    return null
  }
})

const trackLoginNudge = function () {
  window.mixpanel.track('login_nudge_click')
}
</script>

<template>
  <div>
    <div class="Stack Stack--small">
      <div class="Purchase__TopLine">
        <div class="Purchase__PriceLine">
          <div>
            <template v-if="subscribeAndSave && selectedSellingPlanId !== null">
              <span class="Purchase__Price" v-if="subscribePrice !== null">{{
                money(subscribePrice)
              }}</span>
              <span v-if="subscribeAndSave" class="Purchase__PriceCompare"
                ><s>{{ money(price) }}</s></span
              >
              <span v-if="compareAtPrice" class="Purchase__PriceCompare"
                ><s>{{ money(compareAtPrice) }}</s></span
              >
            </template>
            <template v-else>
              <span class="Purchase__Price">{{ money(price) }}</span>
              <span v-if="compareAtPrice" class="Purchase__PriceCompare"
                ><s>{{ money(compareAtPrice) }}</s></span
              >
            </template>
          </div>
          <div>
            <template v-if="subscribeAndSave && selectedSellingPlanId !== null">
              <span
                v-if="pricePer && pricePer.subscribePrice"
                class="Purchase__PricePer"
              >
                {{ money(pricePer.subscribePrice) }} / {{ pricePer.unit }}
              </span>
            </template>
            <template v-else>
              <span v-if="pricePer" class="Purchase__PricePer"
                >{{ money(pricePer.price) }} / {{ pricePer.unit }}</span
              >
            </template>
          </div>
        </div>
        <div class="Purchase__StarRating">
          <TrustpilotStars
            :sku="
              Object.keys(variants)
                .map((v) => variants[v].sku)
                .concat(relatedProductReviews ?? [])
                .join(',')
            "
          />
        </div>
      </div>
      <template v-if="userLoadState === 'LOADING'">
        <purchase-loading />
      </template>
      <template v-else-if="userLoadState === 'ERROR'">
        <bwr-message mode="error">
          <template #icon>
            <icon-status-error />
          </template>
          <template #content>
            <p>
              Sorry, there was an error loading your options. Please refresh the
              page and try again.
            </p>
          </template>
        </bwr-message>
      </template>
      <template v-else-if="subscriptionToThisProduct">
        <div class="Stack Stack--small">
          <div
            v-if="showInlineVariantSelector && Object.keys(variants).length > 1"
            class="Purchase__Options"
          >
            <span v-if="variantOptionLabel" class="Purchase__OptionsLabel">
              Choose {{ variantOptionLabel.toLowerCase() }}
            </span>
            <div class="Purchase__Option Purchase__Option--wide">
              <bwr-select
                :value="selectedVariantId"
                :disabled="purchaseState === 'IN_PROGRESS'"
                @change="onVariantChange($event)"
              >
                <option
                  v-for="variantId in Object.keys(variants)"
                  :key="variantId"
                  :value="variantId"
                >
                  {{ variants[variantId].title }} –
                  {{ money(variants[variantId].price) }}
                </option>
              </bwr-select>
            </div>
          </div>
          <div class="Stack Stack--tiny">
            <p><strong>You’re already subscribed to this product</strong></p>
            <p>
              We'll send this in your order that dispatches on
              {{ nextDeliveryOfThisProduct }}
            </p>
          </div>
          <div class="Stack Stack--small">
            <bwr-button
              :href="`${customerPortalLink}?redirect_to=${encodeURIComponent(
                '/product-subscriptions',
              )}`"
              :modifiers="['xs']"
            >
              <span class="Button__Icon Button__Icon--before"
                ><IconPencil />
              </span>
              Manage your subscription
            </bwr-button>
            <div v-if="!inTab" class="Stack Stack--small">
              <div class="Divider" />
              <p>Need a one-off delivery today?</p>
              <bwr-button
                :modifiers="['xs', 'third-width']"
                :loading="purchaseState === 'IN_PROGRESS'"
                :disabled="purchaseState === 'COMPLETE'"
                @click="
                  emit('purchase', {
                    quantity: 1,
                    when: 'now',
                  })
                "
              >
                <template v-if="purchaseState === 'COMPLETE'">
                  <icon-tick :animate="true" />
                  Added
                </template>
                <template v-else> Add to basket </template>
              </bwr-button>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="Stack Stack--small">
          <div
            v-if="showInlineVariantSelector && Object.keys(variants).length > 1"
            class="Purchase__Options"
          >
            <span v-if="variantOptionLabel" class="Purchase__OptionsLabel">
              Choose {{ variantOptionLabel.toLowerCase() }}
            </span>
            <div class="Purchase__Option Purchase__Option--wide">
              <bwr-select
                :value="selectedVariantId"
                :disabled="purchaseState === 'IN_PROGRESS'"
                @change="onVariantChange($event)"
              >
                <option
                  v-for="variantId in Object.keys(variants)"
                  :key="variantId"
                  :value="variantId"
                >
                  {{ variants[variantId].title }} –
                  {{ money(variants[variantId].price) }}
                </option>
              </bwr-select>
            </div>
          </div>
          <div class="Purchase__Options">
            <span class="Purchase__OptionsLabel">Send me</span>
            <div class="Purchase__Option Purchase__Option--narrow">
              <QuantityControl
                :value="quantity"
                :is-busy="false"
                :min-value="1"
                :max-value="10"
                @change-quantity="onQuantityChange($event)"
              ></QuantityControl>
            </div>
            <div class="Purchase__Option Purchase__Option--wide">
              <div class="FormField FormField--dropdown">
                <bwr-select
                  :value="
                    selectedSellingPlanId ? selectedSellingPlanId : 'NONE'
                  "
                  :disabled="purchaseState === 'IN_PROGRESS'"
                  @change="
                    onSellingPlanChange($event === 'NONE' ? null : $event)
                  "
                >
                  <option value="NONE">once only</option>
                  <option
                    v-for="sellingPlanAllocation in sellingPlanAllocations"
                    :key="sellingPlanAllocation.sellingPlanGroupId"
                    :value="sellingPlanAllocation.sellingPlan.id.toString()"
                  >
                    {{
                      sellingPlanAllocation.sellingPlan.name
                        .replace(/^delivery /i, '')
                        .replace('1 ', '')
                        .toLowerCase()
                    }}
                  </option>
                </bwr-select>
              </div>
            </div>
          </div>
        </div>
        <bwr-radio
          v-if="hasSubscriptions"
          name="when"
          :value="when"
          :options="[
            {
              label: `Add to next order (${nextOrderDate})`,
              value: 'next',
              link: { text: 'View', href: nextOrderLink },
            },
            { label: 'Add to basket to send now', value: 'now' },
          ]"
          :disabled="purchaseState === 'IN_PROGRESS'"
          @change="onWhenChange($event)"
        />
        <div class="Stack Stack--micro">
          <bwr-message
            class="Message--block"
            v-if="selectedSellingPlanId !== null"
            key="sub-1"
            mode="pale"
          >
            <template #icon>
              <IconThumbsup />
            </template>
            <template #content>
              <p>Edit or cancel your subscription online any time.</p>
            </template>
          </bwr-message>
          <bwr-message
            class="Message--block"
            v-else-if="
              savePercent > 0 &&
              sellingPlanAllocations &&
              sellingPlanAllocations.length > 0
            "
            key="sub-2"
            mode="mid"
            :clickable="purchaseState !== 'IN_PROGRESS'"
            @click="
              sellingPlanAllocations &&
                sellingPlanAllocations.length > 0 &&
                onSellingPlanChange(
                  sellingPlanAllocations[0].sellingPlan.id.toString(),
                )
            "
          >
            <template #icon>
              <icon-percent />
            </template>
            <template #content>
              <p>
                Save {{ savePercent }}% when you subscribe to a regular delivery
                – cancel any time, no commitment.
              </p>
            </template>
          </bwr-message>
          <bwr-message
            class="Message--block"
            v-else-if="
              sellingPlanAllocations && sellingPlanAllocations.length > 0
            "
            key="sub-3"
            mode="mid"
            :clickable="purchaseState !== 'IN_PROGRESS'"
            @click="
              sellingPlanAllocations &&
                sellingPlanAllocations.length > 0 &&
                onSellingPlanChange(
                  sellingPlanAllocations[0].sellingPlan.id.toString(),
                )
            "
          >
            <template #icon>
              <icon-subscription />
            </template>
            <template #content>
              <p>
                Subscribe to this product so you never run out - cancel any
                time, no commitment!
              </p>
            </template>
          </bwr-message>
          <bwr-message
            mode="pale"
            class="Message--block"
            v-if="plasticWasteSaved && plasticWasteSaved > 0"
          >
            <template #icon>
              <icon-plastic-bottle />
            </template>
            <template #content>
              <p>
                Our customers are saving
                {{ plasticWasteSaved.toFixed(0) }}kg of plastic waste a year by
                subscribing to this product.
              </p>
            </template>
          </bwr-message>
        </div>
        <div class="Stack Stack--tiny">
          <template v-if="!available && preSale && waitlistPageUrl">
            <bwr-message mode="warning">
              <template #icon>
                <icon-status-warning />
              </template>
              <template #content>
                <p>
                  This product is coming soon! Join the waitlist to be the first
                  to know when it launches.
                </p>
              </template>
            </bwr-message>
            <bwr-button :href="waitlistPageUrl" :modifiers="['block']">
              Join the waitlist
            </bwr-button>
          </template>
          <template v-else-if="!available">
            <bwr-message mode="warning">
              <template #icon>
                <icon-status-warning />
              </template>
              <template #content>
                <p>This product is not currently available</p>
              </template>
            </bwr-message>
            <bwr-button
              id="BIS_trigger"
              :modifiers="['block']"
              :extra-attributes="{ 'data-variant-id': selectedVariantId }"
            >
              Notify me when back in stock
            </bwr-button>
          </template>
          <bwr-button
            v-else
            :modifiers="['block']"
            :loading="purchaseState === 'IN_PROGRESS'"
            :disabled="purchaseState === 'COMPLETE'"
            @click="emit('purchase')"
          >
            <template v-if="purchaseState === 'COMPLETE'" #iconBefore>
              <icon-tick :animate="true" />
            </template>
            <template v-if="purchaseState === 'COMPLETE'"> Added </template>
            <template v-else-if="when === 'now' || !hasSubscriptions">
              Add {{ selectedSellingPlanId !== null ? 'subscription' : '' }} to
              basket
            </template>
            <template v-else>
              Add {{ selectedSellingPlanId !== null ? 'subscription' : '' }} to
              order
            </template>
          </bwr-button>
          <template v-if="purchaseState === 'ERROR'">
            <bwr-message mode="error">
              <template #icon>
                <icon-status-error />
              </template>
              <template #content>
                <p>Sorry, there was an error. Please try again.</p>
              </template>
            </bwr-message>
          </template>
          <p
            v-if="!isLoggedIn && inTab"
            class="Purchase__LoginNudge"
            @click="trackLoginNudge"
          >
            Already a subscriber?
            <a class="Link" :href="loginLink">Log in</a> to manage your orders
          </p>
          <p
            v-if="!inTab && showLink"
            class="Purchase__ViewLink"
            @click="emit('viewProduct')"
          >
            or
            <a class="Link" :href="url">view product page</a>
          </p>
        </div>
      </template>
    </div>
  </div>
</template>
